import React, { useEffect, useState } from "react";
import { Save, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { PageTitle, Field, TextInput, TextArea, ImageUpload } from "./shared";

const SECTIONS = [
  {
    key: "home_hero",
    label: "HOME — MAIN BANNER",
    fields: [
      { name: "kicker", label: "KICKER (SMALL TOP LINE)", type: "text" },
      { name: "title", label: "HEADLINE", type: "text" },
      { name: "subtitle", label: "SUBTITLE", type: "textarea" },
      { name: "badge1", label: "STICKER 1", type: "text" },
      { name: "badge2", label: "STICKER 2", type: "text" },
      { name: "cta_label", label: "BUTTON LABEL", type: "text" },
      { name: "cta_link", label: "BUTTON LINK", type: "text" },
      { name: "image", label: "CENTRE LOGO / IMAGE", type: "image" },
    ],
  },
  {
    key: "home_workshop",
    label: "HOME — FOUNDRY WORKSHOP TILE",
    fields: [
      { name: "title", label: "TITLE", type: "text" },
      { name: "subtitle", label: "TEXT", type: "textarea" },
      { name: "cta_label", label: "BUTTON LABEL", type: "text" },
      { name: "image", label: "IMAGE", type: "image" },
    ],
  },
  {
    key: "home_about",
    label: "HOME — ABOUT US BANNER",
    fields: [
      { name: "title", label: "TITLE", type: "text" },
      { name: "body", label: "TEXT", type: "textarea" },
      { name: "cta_label", label: "BUTTON LABEL", type: "text" },
      { name: "image", label: "IMAGE", type: "image" },
    ],
  },
  {
    key: "workshop_page",
    label: "FOUNDRY WORKSHOP PAGE",
    fields: [
      { name: "title", label: "PAGE TITLE", type: "text" },
      { name: "intro", label: "INTRO TEXT", type: "textarea" },
    ],
  },
  {
    key: "bloom_club",
    label: "HERITAGE BLOOM CLUB PAGE",
    fields: [
      { name: "title", label: "TITLE", type: "text" },
      { name: "intro", label: "INTRO TEXT", type: "textarea" },
      { name: "link", label: "LINK URL", type: "text" },
      { name: "cta_label", label: "BUTTON LABEL", type: "text" },
      { name: "image", label: "IMAGE", type: "image" },
    ],
  },
  {
    key: "contact",
    label: "CONTACT + ABOUT PAGE",
    fields: [
      { name: "about_title", label: "ABOUT TITLE", type: "text" },
      { name: "about_body", label: "ABOUT TEXT", type: "textarea" },
      { name: "email", label: "EMAIL", type: "text" },
      { name: "instagram", label: "INSTAGRAM", type: "text" },
      { name: "facebook", label: "FACEBOOK", type: "text" },
      { name: "location", label: "LOCATION", type: "text" },
      { name: "image", label: "IMAGE", type: "image" },
    ],
  },
  {
    key: "footer",
    label: "FOOTER & TERMS",
    fields: [
      { name: "tagline", label: "FOOTER TAGLINE", type: "text" },
      { name: "email", label: "EMAIL", type: "text" },
      { name: "instagram", label: "INSTAGRAM", type: "text" },
      { name: "facebook", label: "FACEBOOK", type: "text" },
      { name: "location", label: "LOCATION", type: "text" },
      { name: "terms", label: "TERMS AND CONDITIONS", type: "textarea", rows: 6 },
    ],
  },
];

const SectionEditor = ({ section, initial, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(initial || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setData(initial || {}); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${section.key}`, data);
      toast.success(`${section.label} saved`);
      onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] overflow-hidden" data-testid={`editor-section-${section.key}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-6 py-5 text-left" data-testid={`editor-toggle-${section.key}`}>
        <span className="font-serif-display uppercase text-plum-deep text-lg tracking-[0.06em]">{section.label}</span>
        {open ? <ChevronUp className="w-5 h-5 text-plum-deep/50" /> : <ChevronDown className="w-5 h-5 text-plum-deep/50" />}
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-plum-deep/10 pt-5 space-y-4">
          {section.fields.map((f) => (
            <Field key={f.name} label={f.label}>
              {f.type === "textarea" ? (
                <TextArea rows={f.rows || 3} value={data[f.name] || ""} onChange={(e) => setData({ ...data, [f.name]: e.target.value })} data-testid={`editor-${section.key}-${f.name}`} />
              ) : f.type === "image" ? (
                <ImageUpload value={data[f.name]} onChange={(url) => setData({ ...data, [f.name]: url })} testid={`editor-${section.key}-${f.name}`} />
              ) : (
                <TextInput value={data[f.name] || ""} onChange={(e) => setData({ ...data, [f.name]: e.target.value })} data-testid={`editor-${section.key}-${f.name}`} />
              )}
            </Field>
          ))}
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum disabled:opacity-50" data-testid={`editor-save-${section.key}`}>
            <Save className="w-4 h-4" /> {saving ? "SAVING…" : "SAVE SECTION"}
          </button>
        </div>
      )}
    </div>
  );
};

const SiteEditor = () => {
  const { refresh } = useSite();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/admin/content").then((r) => setContent(r.data)).catch(() => setContent({}));
  }, []);

  if (!content) return <p className="text-plum-deep/60">Loading site content…</p>;

  return (
    <div data-testid="console-site-editor">
      <PageTitle kicker="WEBSITE" title="SITE EDITOR" />
      <p className="text-sm text-plum-deep/60 -mt-4 mb-6 max-w-2xl">
        Every banner, image and block of text on the public website is editable here. Changes go live as soon as you save.
      </p>
      <div className="space-y-4 max-w-3xl">
        {SECTIONS.map((s) => (
          <SectionEditor key={s.key} section={s} initial={content[s.key]} onSaved={refresh} />
        ))}
      </div>
    </div>
  );
};

export default SiteEditor;
